import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

// Externals
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import compose from 'recompose/compose';

// Material helpers
import { withStyles } from '@material-ui/core/styles';

// Material components
import Avatar from '@material-ui/core/Avatar';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import TablePagination from '@material-ui/core/TablePagination';
import Tooltip from '@material-ui/core/Tooltip';

// Material icons
import EditIcon from '@material-ui/icons/EditOutlined';
import CheckIcon from '@material-ui/icons/CheckOutlined';
import WarningIcon from '@material-ui/icons/WarningOutlined';

// Shared helpers
import getInitials from 'helpers/getInitials';

// Shared components
import Portlet from 'components/Portlet';
import PortletContent from 'components/PortletContent';

// Common
import * as roles from 'common/roles';

// Translations
import translate from 'helpers/translate';

// Component styles
import styles from './styles';

class UsersTable extends Component {
  state = {
    selectedUsers: [],
    rowsPerPage: 10,
    page: 0
  };

  handleSelectAll = event => {
    const { users, onSelect } = this.props;
    
    let selectedUsers;

    if (event.target.checked) {
      selectedUsers = users.map(user => user._id);
    } else {
      selectedUsers = [];
    }

    this.setState({ selectedUsers });

    onSelect(selectedUsers);
  };

  handleSelectOne = (event, id) => {
    const { onSelect } = this.props;
    const { selectedUsers } = this.state;

    const selectedIndex = selectedUsers.indexOf(id);
    let newSelectedUsers = [];

    if (selectedIndex === -1) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers, id);
    } else if (selectedIndex === 0) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelectedUsers = newSelectedUsers.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedUsers = newSelectedUsers.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1)
      );
    }

    this.setState({ selectedUsers: newSelectedUsers });

    onSelect(newSelectedUsers);
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  renderAccessLevel = accessLevel => {
    const { strings } = this.props;

    switch (accessLevel) {
      case 1:
        return strings.accessLevels.basic;
      case 2:
        return strings.accessLevels.complete;
      case 3:
        return strings.accessLevels.creditcard;
      default:
        return strings.common.unknown;
    }
  };

  renderOrganizationRole = organizationRoles => {
    const { strings } = this.props;

    var organizationRole = organizationRoles.find(r => r.organization_id === this.props.currentOrganization._id);
    switch (organizationRole.role) {
      case 'User':
        return strings.roles.user;
      case 'Transporter':
        return strings.roles.transporter;
      case 'Operator':
        return strings.roles.operator;
      default:
        return 'Inconnu';
    }
  }

  render() {
    const { classes, className, users, strings, role } = this.props;
    const { selectedUsers, rowsPerPage, page } = this.state;

    const rootClassName = classNames(classes.root, className);

    return (
      <Portlet className={rootClassName}>
        <PortletContent noPadding>
          <PerfectScrollbar>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.length === users.length}
                      color="primary"
                      indeterminate={
                        selectedUsers.length > 0 &&
                        selectedUsers.length < users.length
                      }
                      onChange={this.handleSelectAll}
                    />
                  </TableCell>
                  {role === roles.Admin && (
                    <TableCell align="center">{strings.actions}</TableCell>
                  )}
                  <TableCell align="left" padding="none">
                    {strings.name}
                  </TableCell>
                  <TableCell align="left">{strings.email}</TableCell>
                  <TableCell align="left">{strings.address}</TableCell>
                  <TableCell align="left">{strings.phone}</TableCell>
                  <TableCell align="left">{strings.accessLevel}</TableCell>
                  <TableCell align="left">{strings.role}</TableCell>
                  <TableCell align="left">{strings.registrationDate}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users
                  .sort((a, b) => (a.email.toLowerCase() > b.email.toLowerCase()) ? 1 : -1)
                  .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
                  .map(user => (
                    <TableRow
                      className={classes.tableRow}
                      hover
                      key={user._id}
                      selected={selectedUsers.indexOf(user._id) !== -1}
                    >
                      <TableCell padding="checkbox">
                        <div className={classes.tableCellInner}>
                          <Checkbox
                            checked={selectedUsers.indexOf(user._id) !== -1}
                            color="primary"
                            onChange={event =>
                              this.handleSelectOne(event, user._id)
                            }
                            value="true"
                          />
                        </div>
                      </TableCell>
                      {role === roles.Admin && (
                        <TableCell align="center" className={classes.tableCell}>
                          <Link to={'/user/' + user._id}>
                            <IconButton title={strings.userdetails}>
                              <EditIcon />
                            </IconButton>
                          </Link>
                        </TableCell>
                      )}
                      <TableCell className={classes.tableCell} padding="none">
                        <Avatar className={classes.avatar} src={user.avatarUrl}>
                          {getInitials(`${user.firstname} ${user.name}`)}
                        </Avatar>
                        <Typography
                          className={classes.nameText}
                          variant="body1"
                        >
                          {`${user.firstname} ${user.name}`}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <div className={classes.iconText}>
                          {user.isEmailVerified ? 
                            (<Tooltip title={strings.common.emailVerified}>
                              <CheckIcon className={classes.greenText}  />
                            </Tooltip>) : 
                            (<Tooltip title={strings.common.emailNotVerified}>
                              <WarningIcon className={classes.redText} />
                            </Tooltip>)
                          }
                          &nbsp;{user.email ? user.email : '/'}
                        </div>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {`${user.street ? user.street : '/'}, ${
                          user.postalCode ? user.postalCode : '/'
                        } ${user.city ? user.city : '/'}`}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {user.phone ? user.phone : '/'}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {this.renderAccessLevel(user.accessLevel)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {this.renderOrganizationRole(user.organizationRoles)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {moment(user.createdAt).format('DD/MM/YYYY')}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </PerfectScrollbar>
          <TablePagination
            backIconButtonProps={{
              'aria-label': strings.common.previousPage
            }}
            component="div"
            count={users.length}
            labelDisplayedRows={({ from, to, count }) => {
              return from + '-' + to + ' ' + strings.common.of + ' ' + count;
            }}
            labelRowsPerPage={strings.common.rowsPerPage}
            nextIconButtonProps={{
              'aria-label': strings.common.nextPage
            }}
            onChangePage={this.handleChangePage}
            onChangeRowsPerPage={this.handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </PortletContent>
      </Portlet>
    );
  }
}

UsersTable.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onSelect: PropTypes.func,
  onShowDetails: PropTypes.func,
  users: PropTypes.array.isRequired
};

UsersTable.defaultProps = {
  users: [],
  onSelect: () => {},
  onShowDetails: () => {}
};

const mapStateToProps = state => {
  return {
    currentOrganization: state.organization.currentOrganization,
    role: state.authorize.role
  };
};

const mapDispatchToProps = {};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  translate('UsersTable'),
  withStyles(styles)
)(UsersTable);
